"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Copy, FileUp, Download } from "lucide-react"
import { toast } from "sonner"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export function JsonFormatter() {
  const [inputJson, setInputJson] = useState("")
  const [outputJson, setOutputJson] = useState("")
  const [indentSize, setIndentSize] = useState(2)
  const [error, setError] = useState<string | null>(null)

  const formatJson = () => {
    try {
      const parsed = JSON.parse(inputJson)
      const formatted = JSON.stringify(parsed, null, indentSize)
      setOutputJson(formatted)
      setError(null)
      toast.success("JSON formatted successfully")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid JSON")
      setOutputJson("")
      toast.error(`Invalid JSON: ${err instanceof Error ? err.message : "Please check your JSON syntax"}`)
    }
  }

  const minifyJson = () => {
    try {
      const parsed = JSON.parse(inputJson)
      const minified = JSON.stringify(parsed)
      setOutputJson(minified)
      setError(null)
      toast.success("JSON minified successfully")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid JSON")
      setOutputJson("")
      toast.error(`Invalid JSON: ${err instanceof Error ? err.message : "Please check your JSON syntax"}`)
    }
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      setInputJson(reader.result as string)
    }
    reader.readAsText(file)
  }

  const downloadJson = () => {
    if (!outputJson) return

    const blob = new Blob([outputJson], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "formatted.json"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast.success("Download started")
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(outputJson)
    toast.success("Copied to clipboard!")
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>JSON Formatter</CardTitle>
        <CardDescription>Format, validate, and minify JSON data. Upload JSON files or paste directly.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Input JSON</Label>
            <Textarea
              placeholder="Paste your JSON here..."
              className="min-h-[200px] md:min-h-[300px] font-mono text-sm"
              value={inputJson}
              onChange={(e) => setInputJson(e.target.value)}
            />
            <div className="flex justify-between">
              <Button variant="outline" size="sm" onClick={() => setInputJson("")}>
                Clear
              </Button>
              <div className="relative">
                <input
                  type="file"
                  id="jsonUpload"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  accept="application/json"
                  onChange={handleFileUpload}
                />
                <Button variant="outline" size="sm">
                  <FileUp className="h-4 w-4 mr-2" />
                  Upload JSON
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Output</Label>
            <Textarea
              placeholder="Formatted JSON will appear here..."
              className="min-h-[200px] md:min-h-[300px] font-mono text-sm"
              value={outputJson}
              readOnly
            />
            <div className="flex justify-between">
              <Button variant="outline" size="sm" onClick={copyToClipboard} disabled={!outputJson}>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
              <Button variant="outline" size="sm" onClick={downloadJson} disabled={!outputJson}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <div className="space-y-2 w-full sm:w-auto">
            <Label htmlFor="indent-size">Indent Size</Label>
            <Input
              id="indent-size"
              type="number"
              min="0"
              max="8"
              value={indentSize}
              onChange={(e) => setIndentSize(Number.parseInt(e.target.value) || 0)}
              className="w-full sm:w-20"
            />
          </div>

          <div className="flex gap-2 w-full sm:w-auto">
            <Button onClick={formatJson} className="flex-1 sm:flex-initial">
              Format JSON
            </Button>
            <Button onClick={minifyJson} variant="outline" className="flex-1 sm:flex-initial">
              Minify JSON
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

